/*
 * cropTo916.js — client-side "Auto-crop to 9:16" for uploaded videos.
 *
 * Strategy:
 *   1. Load the input File into a hidden <video>.
 *   2. Draw a 720×1280 canvas showing only the centered 9:16 slice.
 *   3. Record the canvas via MediaRecorder + the original audio track,
 *      producing a new File the caller can upload.
 *
 * Constraints:
 *   - Uses browser-native APIs (no ffmpeg-wasm) so it works everywhere modern.
 *   - Emits a webm file (VP9/opus). Backend already accepts webm.
 *   - Real-time capture — a 30-second clip takes ~30s to process. We surface
 *     a progress callback so the caller can render a spinner.
 */

const TARGET_W = 720;
const TARGET_H = 1280;

// Seek helper: resolves once the frame at `t` is decoded.
function seekTo(video, t) {
  return new Promise((res) => {
    const onSeeked = () => { video.removeEventListener("seeked", onSeeked); res(); };
    video.addEventListener("seeked", onSeeked);
    video.currentTime = Math.max(0, t);
  });
}

export function isBrowserCropSupported() {
  try {
    if (typeof MediaRecorder === "undefined") return false;
    // Prefer webm/vp9 → webm/vp8 → any webm. Fail if none supported.
    return ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"].some(t => MediaRecorder.isTypeSupported(t));
  } catch { return false; }
}

export async function cropVideoTo916(file, { onProgress } = {}) {
  if (!isBrowserCropSupported()) throw new Error("Browser doesn't support in-browser video crop");
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  await new Promise((res, rej) => {
    video.onloadedmetadata = res;
    video.onerror = () => rej(new Error("Could not read video file"));
  });
  const duration = video.duration || 0;
  const iw = video.videoWidth || 1;
  const ih = video.videoHeight || 1;

  // Compute source rectangle for a 9:16 crop.
  // Base: center-crop. If FaceDetector is available, seek to 30% of the clip,
  // detect faces, and center the crop around the largest face's x-center.
  let sx, sy, sw, sh;
  const inRatio = iw / ih;
  const targetRatio = TARGET_W / TARGET_H; // 0.5625
  if (inRatio > targetRatio) {
    sh = ih;
    sw = Math.round(ih * targetRatio);
    sx = Math.round((iw - sw) / 2);
    sy = 0;
    // Face-aware: try to bias sx toward detected face center
    try {
      if ("FaceDetector" in window) {
        const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 3 });
        const probeAt = Math.max(0.1, Math.min((duration || 1) * 0.3, (duration || 1) - 0.1));
        await seekTo(video, probeAt);
        // Draw current frame into a canvas so FaceDetector can inspect it
        const probe = document.createElement("canvas");
        probe.width = iw; probe.height = ih;
        probe.getContext("2d").drawImage(video, 0, 0, iw, ih);
        const faces = await detector.detect(probe);
        if (faces && faces.length) {
          // Pick the largest face
          faces.sort((a, b) => (b.boundingBox.width * b.boundingBox.height) - (a.boundingBox.width * a.boundingBox.height));
          const bb = faces[0].boundingBox;
          const cx = bb.x + bb.width / 2;
          const desiredSx = Math.round(cx - sw / 2);
          sx = Math.max(0, Math.min(iw - sw, desiredSx));
        }
        await seekTo(video, 0); // rewind for the recording pass
      }
    } catch { /* face detection is best-effort */ }
  } else {
    sw = iw;
    sh = Math.round(iw / targetRatio);
    sx = 0;
    sy = Math.round((ih - sh) / 2);
    // Face-aware vertical bias — tall videos (rare here) still benefit
    try {
      if ("FaceDetector" in window) {
        const detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 3 });
        const probeAt = Math.max(0.1, Math.min((duration || 1) * 0.3, (duration || 1) - 0.1));
        await seekTo(video, probeAt);
        const probe = document.createElement("canvas");
        probe.width = iw; probe.height = ih;
        probe.getContext("2d").drawImage(video, 0, 0, iw, ih);
        const faces = await detector.detect(probe);
        if (faces && faces.length) {
          faces.sort((a, b) => (b.boundingBox.width * b.boundingBox.height) - (a.boundingBox.width * a.boundingBox.height));
          const bb = faces[0].boundingBox;
          const cy = bb.y + bb.height / 2;
          const desiredSy = Math.round(cy - sh / 2);
          sy = Math.max(0, Math.min(ih - sh, desiredSy));
        }
        await seekTo(video, 0);
      }
    } catch { /* best-effort */ }
  }

  // Canvas + drawing loop
  const canvas = document.createElement("canvas");
  canvas.width = TARGET_W;
  canvas.height = TARGET_H;
  const ctx = canvas.getContext("2d");

  // MediaRecorder setup — canvas stream + original audio
  const canvasStream = canvas.captureStream(30);
  let audioTracks = [];
  try {
    // captureStream on <video> gives audio track from the source
    if (typeof video.captureStream === "function") {
      const vs = video.captureStream();
      audioTracks = vs.getAudioTracks();
    }
  } catch { /* ignore — silent output */ }
  audioTracks.forEach(t => canvasStream.addTrack(t));

  const mime = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
    .find(t => MediaRecorder.isTypeSupported(t));
  const recorder = new MediaRecorder(canvasStream, { mimeType: mime, videoBitsPerSecond: 3_500_000 });
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

  const drawFrame = () => {
    if (video.paused || video.ended) return;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, TARGET_W, TARGET_H);
    if (onProgress && duration) onProgress(Math.min(1, video.currentTime / duration));
    requestAnimationFrame(drawFrame);
  };

  await new Promise((resolve, reject) => {
    recorder.onstop = resolve;
    recorder.onerror = (e) => reject(e.error || new Error("Recording failed"));
    recorder.start();
    video.play().catch(reject);
    // First frame kicks off the RAF loop
    video.onplay = () => requestAnimationFrame(drawFrame);
    video.onended = () => {
      // Flush + stop
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, TARGET_W, TARGET_H);
      setTimeout(() => recorder.state !== "inactive" && recorder.stop(), 60);
    };
    // Safety timeout — 4 minutes cap
    setTimeout(() => { if (recorder.state !== "inactive") recorder.stop(); }, 4 * 60 * 1000);
  });

  URL.revokeObjectURL(url);
  const blob = new Blob(chunks, { type: "video/webm" });
  const cleanName = (file.name || "video").replace(/\.[a-z0-9]+$/i, "") + "-9x16.webm";
  return new File([blob], cleanName, { type: "video/webm", lastModified: Date.now() });
}
